/**
 * Layout constants for viral recording flow diagrams
 */

export const LAYOUT_CONSTANTS = {
  // Node dimensions
  START_EVENT_WIDTH: 120,
  START_EVENT_HEIGHT: 80,
  GROUP_NODE_WIDTH: 760,
  GROUP_NODE_HEIGHT: 950,
  STEP_CARD_WIDTH: 760,
  STEP_CARD_HEIGHT: 600,
  WHO_ELSE_WIDTH: 760,
  WHO_ELSE_HEIGHT: 300,
  STEP_WITH_WHO_ELSE_WIDTH: 760,
  STEP_WITH_WHO_ELSE_HEIGHT: 950,
  SUCCESSOR_CARD_WIDTH: 424,
  SUCCESSOR_CARD_BASE_HEIGHT: 100,

  // Card heights for successor cards
  PERSON_CARD_HEIGHT: 110,
  SYSTEM_CARD_HEIGHT: 110,
  EXTERNAL_CONTACT_CARD_HEIGHT: 90,
  SHARED_INBOX_CARD_HEIGHT: 90,
  RULE_HEADER_HEIGHT: 40,
  CONTENT_PADDING: 50,

  // Horizontal layout gaps
  HORIZONTAL_GAP: 100,

  // Step handle Y offsets (distance from step node top to where edge should connect)
  // These values position the edge to align with the step card's output handle
  DECISION_STEP_HANDLE_OFFSET: 350,
  TASK_STEP_HANDLE_OFFSET: 550,

  // Y position for preview (StartEventNode) and task successor alignment
  PREVIEW_Y: 150,

  // Auto-save debounce time
  AUTO_SAVE_DEBOUNCE_MS: 2000
};

/**
 * Get node dimensions based on node type
 */
export const getNodeDimensions = (
  nodeType: string,
  nodeData?: Record<string, unknown>
): { width: number; height: number } => {
  switch (nodeType) {
    case 'startEvent': {
      // Calculate dimensions based on whether there's a predecessor preview
      const hasPredecessorPreview =
        nodeData?.predecessorStep &&
        typeof nodeData.predecessorStep === 'object' &&
        nodeData.predecessorStep !== null &&
        '_id' in (nodeData.predecessorStep as object);

      if (hasPredecessorPreview) {
        // Preview replaces the circle: 340px wide, ~200px tall
        // (includes user info + step type + inputs + description + output sections)
        return {
          width: 340,
          height: 200
        };
      }

      // Original circle dimensions
      return {
        width: LAYOUT_CONSTANTS.START_EVENT_WIDTH,
        height: LAYOUT_CONSTANTS.START_EVENT_HEIGHT
      };
    }
    case 'group':
      return {
        width: LAYOUT_CONSTANTS.GROUP_NODE_WIDTH,
        height: LAYOUT_CONSTANTS.GROUP_NODE_HEIGHT
      };
    case 'stepCard':
      return {
        width: LAYOUT_CONSTANTS.STEP_CARD_WIDTH,
        height: LAYOUT_CONSTANTS.STEP_CARD_HEIGHT
      };
    case 'whoElseDoesThis':
      return {
        width: LAYOUT_CONSTANTS.WHO_ELSE_WIDTH,
        height: LAYOUT_CONSTANTS.WHO_ELSE_HEIGHT
      };
    case 'stepWithWhoElse':
      return {
        width: LAYOUT_CONSTANTS.STEP_WITH_WHO_ELSE_WIDTH,
        height: LAYOUT_CONSTANTS.STEP_WITH_WHO_ELSE_HEIGHT
      };
    case 'successorCard':
      return {
        width: LAYOUT_CONSTANTS.SUCCESSOR_CARD_WIDTH,
        height: calculateSuccessorCardHeight(nodeData)
      };
    case 'successorsColumn':
      // CSS flexbox handles internal layout, just provide base dimensions
      return {
        width: LAYOUT_CONSTANTS.SUCCESSOR_CARD_WIDTH,
        height: LAYOUT_CONSTANTS.SUCCESSOR_CARD_BASE_HEIGHT
      };
    default:
      return { width: 200, height: 100 };
  }
};

interface SuccessorCardData {
  value?: {
    persons?: unknown[];
    systems?: unknown[];
    externalContacts?: unknown[];
    sharedInboxes?: unknown[];
  };
  ruleNumber?: number;
}

/**
 * Calculate dynamic height for successor cards based on content
 */
export const calculateSuccessorCardHeight = (nodeData?: Record<string, unknown>): number => {
  const baseHeight = LAYOUT_CONSTANTS.SUCCESSOR_CARD_BASE_HEIGHT;
  let contentHeight = 0;

  const data = nodeData as SuccessorCardData | undefined;

  if (data?.value) {
    contentHeight += (data.value.persons?.length || 0) * LAYOUT_CONSTANTS.PERSON_CARD_HEIGHT;
    contentHeight += (data.value.systems?.length || 0) * LAYOUT_CONSTANTS.SYSTEM_CARD_HEIGHT;
    contentHeight +=
      (data.value.externalContacts?.length || 0) * LAYOUT_CONSTANTS.EXTERNAL_CONTACT_CARD_HEIGHT;
    contentHeight +=
      (data.value.sharedInboxes?.length || 0) * LAYOUT_CONSTANTS.SHARED_INBOX_CARD_HEIGHT;
  }

  if (data?.ruleNumber) {
    contentHeight += LAYOUT_CONSTANTS.RULE_HEADER_HEIGHT;
  }

  if (contentHeight > 0) {
    contentHeight += LAYOUT_CONSTANTS.CONTENT_PADDING;
  }

  return Math.max(baseHeight, baseHeight + contentHeight);
};
